import React, { useImperativeHandle, forwardRef, useRef, type CSSProperties, type SyntheticEvent } from 'react';

interface PaperSize {
  // 部分 caller 在 template 尚未載入時會傳入 undefined；fallback 由內部 CSS variables 處理
  width?: number;
  height?: number;
}

interface TemplateIframeProps {
  html: string;
  paperSize: PaperSize;
  cssVariablePrefix?: string;
  style?: CSSProperties;
  title?: string;
  onLoad?: (e: SyntheticEvent<HTMLIFrameElement>) => void;
}

/**
 * TemplateIframe - Reusable iframe component for rendering Liquid templates
 *
 * @param props.html - Rendered HTML content
 * @param props.paperSize - Paper size { width, height } in mm
 * @param props.cssVariablePrefix - CSS variable prefix ('paper' or 'label')
 * @param props.style - Additional styles for iframe
 * @param props.title - Iframe title (default: 'Template Preview')
 * @param props.onLoad - Optional onLoad callback
 * @param ref - Ref to iframe element
 */
const TemplateIframe = forwardRef<HTMLIFrameElement, TemplateIframeProps>(({
  html,
  paperSize,
  cssVariablePrefix = 'paper',
  style = {},
  title = 'Template Preview',
  onLoad
}, ref) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Expose iframe ref to parent
  useImperativeHandle(ref, () => iframeRef.current as HTMLIFrameElement);

  const handleLoad = (e: SyntheticEvent<HTMLIFrameElement>) => {
    try {
      const iframe = e.target as HTMLIFrameElement;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow!.document;

      if (iframeDoc?.body) {
        // Inject CSS variables for dynamic paper size
        iframeDoc.body.style.setProperty(`--${cssVariablePrefix}-width`, `${paperSize.width}mm`);
        iframeDoc.body.style.setProperty(`--${cssVariablePrefix}-height`, `${paperSize.height}mm`);

        // Auto-adjust iframe height based on content
        const contentHeight = iframeDoc.body.scrollHeight;
        iframe.style.height = `${contentHeight + 20}px`;
      }

      // Call optional onLoad callback
      if (onLoad) {
        onLoad(e);
      }
    } catch (err) {
      console.error('Failed to adjust iframe:', err);
    }
  };

  return (
    <iframe
      ref={iframeRef}
      srcDoc={html}
      style={{
        border: 'none',
        width: `${paperSize.width}mm`,
        minHeight: `${paperSize.height}mm`,
        backgroundColor: 'white',
        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
        ...style
      }}
      title={title}
      onLoad={handleLoad}
    />
  );
});

TemplateIframe.displayName = 'TemplateIframe';

export default TemplateIframe;
